import React from 'react'

import { Five9CustomFieldsEditor } from '@/views/Integrations/five9/Five9CustomFieldsEditor'
import { Five9CampaignNameSelect } from '@/views/Integrations/five9/Five9CampaignNameSelect'
import {
  TcnCampaignNameFields,
  CAMPAIGN_FIELDS_SCHEMA,
} from '@/views/Integrations/tcn/TcnCampaignNameFields'
import {
  TcnCallMetadataFields,
  CALL_METADATA_SCHEMA,
} from '@/views/Integrations/tcn/TcnCallMetadataFields'
import { PathField } from '@/views/Integrations/pathMapping/PathField'
import { optionalPathSchema, requiredPathSchema } from '@/views/Integrations/pathMapping/paths'
import {
  IntermediaUniteCampaignNameFields,
  CAMPAIGN_FIELDS_SCHEMA as INTERMEDIA_UNITE_CAMPAIGN_FIELDS_SCHEMA,
} from '@/views/Integrations/intermediaUnite/IntermediaUniteCampaignNameFields'
import { GenericWebhookInstructions } from '@/views/Integrations/generic/GenericWebhookInstructions'
import { GenericCampaignNameFields } from '@/views/Integrations/generic/GenericCampaignNameFields'
import { GenericCallMetadataFields } from '@/views/Integrations/generic/GenericCallMetadataFields'
import { GenericDirectionMapFields } from '@/views/Integrations/generic/GenericDirectionMapFields'
import { GenericEventValuesFields } from '@/views/Integrations/generic/GenericEventValuesFields'
import { GenericSetupGuide } from '@/views/Integrations/generic/GenericSetupGuide'
import {
  CAMPAIGN_PATHS_SCHEMA,
  DIRECTION_MAP_SCHEMA,
  GENERIC_CALL_METADATA_SCHEMA,
  GENERIC_FIELDS,
  DISPOSITION_VALUES_SCHEMA,
  START_VALUES_SCHEMA,
  STOP_VALUES_SCHEMA,
} from '@/views/Integrations/generic/genericFields'

export const getSchema = ({ typeOptions, organizationOptions }) => [
  {
    label: 'Name',
    name: 'name',
    type: 'text',
    required: true,
  },
  {
    label: 'Integration Type',
    name: 'type',
    type: 'select',
    options: typeOptions,
    required: true,
  },
  {
    label: 'Organization',
    name: 'organization_id',
    type: 'select',
    options: organizationOptions,
    required: true,
  },
]
const createStartStopSelect = (defaultValue, enabled) => {
  return [
    {
      label: 'Start Stop Method',
      name: 'remote_id_method',
      type: 'select',
      options: [
        { value: 'by_email', label: 'By Email' },
        { value: 'by_voip_user_id', label: 'By Voip User ID' },
      ],
      defaultValue,
      isDisabled: !enabled,
      required: true,
    },
  ]
}
const baseConnector = [
  {
    label: 'Integration Type',
    name: 'display_type', // this value is ignored in the backend but is used to make it more readable for FE
    type: 'text',
    required: true,
    disabled: true,
  },
  {
    label: 'Organization',
    name: 'organization_id',
    type: 'text',
    required: true,
    disabled: true,
  },
  {
    label: 'Token',
    name: 'token',
    type: 'text',
    required: false,
    disabled: true,
    defaultValue: 'None',
  },
]
const ringcentralSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'JWT',
    name: 'jwt_token',
    type: 'text',
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'id', label: 'ID' },
      { value: 'telephonySessionId', label: 'Telephony Session ID' },
      { value: 'sessionId', label: 'Session ID' },
      { value: 'callerId', label: 'Legacy Phn Num (AIN)' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'telephonySessionId',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'callDirection', label: 'Call Direction' },
      { value: 'repNum', label: 'Internal Phone Number.' },
      { value: 'callD&repNum', label: 'Call Dir. + Rep Num.' },
      { value: 'queueName', label: 'Queue Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'callDirection',
  },
  {
    label: 'Ignore Internal Calls',
    name: 'ignore_internal_calls',
    type: 'checkbox',
    defaultValue: false,
  },
]
const genesysSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'Audio API Key',
    name: 'audio_api_key',
    type: 'text',
    disabled: true,
    required: false,
    nullable: true,
  },
  {
    label: 'Client Id',
    name: 'client_id',
    type: 'text',
    required: true,
  },
  {
    label: 'Client Secret',
    name: 'client_secret',
    type: 'text',
    required: true,
  },
  {
    label: 'Authentication Server',
    name: 'auth_server',
    type: 'text',
    required: true,
  },
  {
    label: 'API Server',
    name: 'api_server',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'uuid', label: 'UUID' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'uuid',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'queue.id', label: 'Queue' },
      { value: 'direction', label: 'Direction' },
      { value: 'queue.id+direction', label: 'Queue + Direction' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'queue.id',
  },
  {
    label: 'Ignore remote start when voip campaign name is empty',
    name: 'ignore_remote_start',
    type: 'checkbox',
    defaultValue: false,
  },
]

const five9Schema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'VOIP User ID Parameter',
    name: 'remote_id_source',
    type: 'select',
    options: [
      { value: 'Agent.id', label: 'Agent ID' },
      { value: 'Agent.user_name', label: 'Agent Username' },
    ],
    defaultValue: 'Agent.id',
    dependencies: [
      {
        key: 'remote_id_method',
        operator: '===',
        value: 'by_voip_user_id',
      },
    ],
  },
  {
    label: 'Five9 Admin Username',
    name: 'username',
    type: 'text',
    required: true,
  },
  {
    label: 'Five9 Admin Password',
    name: 'password',
    type: 'password',
    required: true,
  },
  {
    label: 'Region',
    name: 'region',
    type: 'select',
    options: [
      { value: 'com', label: 'US' },
      { value: 'eu', label: 'EU' },
      { value: 'ca', label: 'Canada' },
    ],
    defaultValue: 'com',
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'Call.session_id', label: 'Session ID' },
      { value: 'Call.call_id', label: 'Call ID' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'Call.call_id',
  },
  {
    raw: true,
    name: 'custom_fields',
    defaultValue: [],
    as: <Five9CustomFieldsEditor key="five9-custom-fields" />,
  },
  {
    raw: true,
    name: 'voip_campaign_name_fields',
    defaultValue: [],
    as: <Five9CampaignNameSelect key="five9-campaign-name" />,
  },
  {
    label: 'Only Stop On Disposition',
    name: 'only_stop_on_dispo',
    type: 'checkbox',
    defaultValue: false,
  },
]

// Five9 pushes Event Subscription Service events to us, so unlike five9Schema there
// are no Five9 admin credentials: the subscription is set up by hand in the Five9
// Admin Console and sends this config's Token in a `token` header.
const five9EssSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      // What the backend uses when voip_call_id is unset, so existing configs show
      // the ID they actually run on and saving one doesn't change it.
      { value: 'payload.interaction_id', label: 'Interaction ID' },
      // The numeric ID the five9 Web Connectors send, for continuity with that connector.
      { value: 'payload.call_id', label: 'Call ID' },
    ],
    defaultValue: 'payload.interaction_id',
  },
  {
    label: 'Start on Ringing',
    name: 'start_on_ringing',
    type: 'checkbox',
    defaultValue: false,
  },
]

const slackSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'Channel',
    name: 'channel',
    type: 'text',
    required: true,
  },
]

const eightByEightSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'Server (ex: vcc-pp33)',
    name: 'dns',
    type: 'text',
    required: true,
  },
  {
    label: '8x8 Admin Username (Tenant Id)',
    name: 'username',
    type: 'text',
    required: true,
  },
  {
    label: '8x8 Data Request Token',
    name: 'password',
    type: 'password',
    required: true,
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'interactionGUID', label: 'Interaction GUID' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'interactionGUID',
  },

  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'tenantSkillName', label: 'Tenant Skill Name' },
      { value: 'que', label: 'Queue' },
      { value: 'queueDirection', label: 'Queue Direction' },
      { value: 'tenantName', label: 'Tenant Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'tenantSkillName',
  },
]

const eightByEightV2Schema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', true),
  {
    label: 'API Key',
    name: 'api_key',
    type: 'password',
    required: true,
  },
  {
    label: 'Tenant',
    name: 'tenant',
    type: 'text',
    required: true,
  },
  {
    label: 'Region',
    name: 'region',
    type: 'select',
    options: [
      { value: 'us1', label: 'US1' },
      { value: 'us2', label: 'US2' },
      { value: 'uk3', label: 'UK3' },
    ],
    required: true,
    defaultValue: 'us1',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'tenantSkillName', label: 'Tenant Skill Name' },
      { value: 'que', label: 'Queue' },
      { value: 'queueDirection', label: 'Queue Direction' },
      { value: 'tenantName', label: 'Tenant Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'tenantSkillName',
  },
  {
    label: 'Transaction Code Translations (JSON)',
    name: 'transaction_code_translations',
    type: 'textArea',
    required: false,
    nullable: true,
    json: true,
    rows: 12,
  },
]

const vonageEnterpriseSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'API Key',
    name: 'api_key',
    type: 'text',
    required: true,
  },
  {
    label: 'API Secret',
    name: 'api_secret',
    type: 'password',
    required: true,
  },
  {
    label: 'Application ID',
    name: 'application_id',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'uuid', label: 'UUID' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'uuid',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'direction',
  },
]

const amazonConnectSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'API Key',
    name: 'api_key',
    type: 'text',
    required: false,
    nullable: true,
  },
  {
    label: 'API Secret',
    name: 'api_secret',
    type: 'password',
    required: false,
    nullable: true,
  },
  {
    label: 'Region',
    name: 'region',
    type: 'select',
    options: [
      { value: 'us-east-1', label: 'us-east-1' },
      { value: 'us-east-2', label: 'us-east-2' },
      { value: 'us-west-1', label: 'us-west-1' },
      { value: 'us-west-2', label: 'us-west-2' },
    ],
    defaultValue: 'us-east-1',
    required: true,
  },
  {
    label: 'Role Arn',
    name: 'role_arn',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'queueInfo', label: 'Queue ARN' },
      { value: 'initiationMethod', label: 'Direction' },
      { value: 'queueInfo_initiationMethod', label: 'Queue ARN + Direction' },
    ],
    defaultValue: 'initiationMethod',
  },
  {
    label: 'External Id',
    name: 'external_id',
    type: 'text',
    required: false,
  },
  {
    label: 'API Destination Role',
    name: 'api_destination_role',
    type: 'text',
    required: false,
  },
]

const zoomSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'Access Token',
    name: 'access_token',
    type: 'text',
    required: true,
  },
  {
    label: 'Refresh Token',
    name: 'refresh_token',
    type: 'text',
    required: true,
  },
  {
    label: 'account_id',
    name: 'account_id',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'call_id', label: 'Call ID' },
      { value: '', label: 'None' },
    ],
    defaultValue: '',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'queue', label: 'Queue' },
      { value: 'direction_queue', label: 'Direction + Queue' },
      { value: '', label: 'None' },
    ],
    defaultValue: '',
  },
]

const zoomContactCenterSchema = [
  ...baseConnector,
  {
    label: 'Start Stop Method',
    name: 'remote_id_method',
    type: 'select',
    options: [
      { value: 'by_user_id', label: 'By User ID' },
      { value: 'by_display_name', label: 'By Display Name' },
    ],
    defaultValue: 'by_user_id',
    isDisabled: false,
    required: true,
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'queue', label: 'Queue' },
      { value: 'direction_queue', label: 'Direction + Queue' },
      { value: '', label: 'None' },
    ],
    defaultValue: '',
  },
  {
    label: 'Secret Token',
    name: 'secret_token',
    type: 'password',
    required: true,
  },
]

const zoomVideoSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'secret_token',
    name: 'secret_token',
    type: 'password',
    required: true,
  },
]

const atscallSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'username',
    name: 'username',
    type: 'text',
    required: true,
  },
  {
    label: 'password',
    name: 'password',
    type: 'password',
    required: true,
  },
  {
    label: 'customer id',
    name: 'customer_id',
    type: 'text',
    required: true,
  },
]

const closeSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'API Key',
    name: 'api_key',
    type: 'text',
    required: true,
  },
]

const aircallSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'team', label: 'Team' },
      { value: 'team_direction', label: 'Team + Direction' },
    ],
    defaultValue: 'direction',
  },
]

const tldSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'list_name', label: 'List Name' },
      { value: 'list_id', label: 'List ID' },
      { value: 'user_group', label: 'User Group' },
      { value: 'group', label: 'Group' },
      { value: 'direction', label: 'Direction' },
      { value: 'type', label: 'Type' },
      { value: 'list_name_plus_direction', label: 'List Name + Direction' },
      { value: 'list_id_plus_direction', label: 'List ID + Direction' },
      { value: 'user_group_plus_direction', label: 'User Group + Direction' },
      { value: 'group_plus_direction', label: 'Group + Direction' },
      { value: 'type_plus_direction', label: 'Type + Direction' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'direction',
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'call_id', label: 'Call ID' },
      { value: 'uniqueid', label: 'Unique ID' },
    ],
    defaultValue: 'call_id',
    disabled: false,
  },
  {
    label: 'VOIP Customer ID',
    name: 'voip_customer_id',
    type: 'select',
    options: [
      { value: 'phone_number', label: 'Phone Number' },
      { value: 'lead_id', label: 'Lead ID' },
      { value: 'vendor_lead_code', label: 'Vendor Lead Code' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'phone_number',
  },
]

const trackdriveSchema = [...baseConnector, ...createStartStopSelect('by_email', false)]

const sinchSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'Current queue ID', label: 'Current queue ID' },
      { value: 'Direction', label: 'Direction' },
      { value: 'CAD.voip_campaign_name', label: 'CAD - Voip Campaign Name' },
    ],
    defaultValue: 'Direction',
  },
]

const thrioSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'campaignName', label: 'Campaign Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'direction',
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'workitemId', label: 'Work Item ID' },
      { value: '', label: 'None' },
    ],
    defaultValue: '',
  },
]

const mightyCallSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'queueName', label: 'Queue Name' },
      { value: 'direction+queueName', label: 'Queue Direction and Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'direction',
  },
]

const gotoSchema = [
  ...baseConnector,
  {
    label: 'Client Id',
    name: 'client_id',
    type: 'text',
    required: true,
    disabled: false,
  },
  {
    label: 'Client Secret',
    name: 'client_secret',
    type: 'text',
    required: true,
    disabled: false,
  },
  {
    label: 'Refresh Token',
    name: 'refresh_token',
    type: 'text',
    required: true,
    disabled: false,
  },
]

const niceSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'Access Key ID',
    name: 'access_key_id',
    type: 'text',
    required: true,
  },
  {
    label: 'Access Key ID Secret',
    name: 'access_key_secret',
    type: 'password',
    required: true,
  },
  {
    label: 'API Server',
    name: 'api_server',
    type: 'text',
    required: true,
  },
  {
    label: 'Audio API Key',
    name: 'audio_api_key',
    type: 'text',
    required: false,
    nullable: true,
  },
  {
    label: 'Audio Connector',
    name: 'audio_enabled',
    type: 'checkbox',
    defaultValue: true,
  },
]

const ringcentralEngageSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Call Direction' },
      { value: 'direction_queue', label: 'Direction+Queue' },
      { value: 'queue', label: 'Queue' },
    ],
    defaultValue: 'queue',
  },
]

const tcnSchema = [
  ...baseConnector,
  // Locked: TCN's agent-state events carry no email, so the connector always
  // starts and stops by agentSid.
  ...createStartStopSelect('by_voip_user_id', false),
  {
    label: 'Client Id',
    name: 'client_id',
    type: 'text',
    required: true,
    disabled: false,
  },
  {
    label: 'Client Secret',
    name: 'client_secret',
    type: 'text',
    required: true,
    disabled: false,
  },
  {
    label: 'Refresh Token',
    name: 'refresh_token',
    type: 'text',
    required: false,
    disabled: false,
    nullable: true,
  },
  {
    label: 'API Token',
    name: 'api_token',
    type: 'text',
    required: false,
    disabled: false,
    nullable: true,
  },
  {
    raw: true,
    name: 'voip_campaign_name_fields',
    defaultValue: [],
    customValidators: [CAMPAIGN_FIELDS_SCHEMA],
    as: <TcnCampaignNameFields key="tcn-campaign-name" />,
  },
  {
    // Named values pulled from the TCN payloads and sent to Remote Desktop as
    // call_metadata, where they land on the call record and can drive dynamic
    // checklists.
    raw: true,
    name: 'call_metadata_fields',
    defaultValue: [],
    customValidators: [CALL_METADATA_SCHEMA],
    as: <TcnCallMetadataFields key="tcn-call-metadata" />,
  },
  {
    // Logs the raw TCN API responses (peer data, client info, call data, user
    // details) so the paths above can be written against what TCN actually sends.
    // Those responses hold customer phone numbers and contact-list fields, so it is
    // off by default and meant to be turned off again once setup is done. Like any
    // TCN setting it applies on the next resubscribe, not on save. PLATFORM-1761.
    label: 'Log API Payloads (customer data, turn off after setup)',
    name: 'log_api_payloads',
    type: 'checkbox',
    defaultValue: false,
  },
  {
    label: 'VOIP Customer ID Source',
    name: 'voip_customer_id_source',
    type: 'select',
    options: [
      { value: 'phone_number', label: 'Phone Number' },
      { value: 'account_number', label: 'Account Number' },
    ],
    defaultValue: 'phone_number',
  },
  {
    // Agent states that stop the call on top of WRAPUP and HUNGUP, which always
    // stop and so are not listed. PEERED is deliberately absent: it starts a call,
    // and the backend ignores it here even if a row somehow contains it.
    label: 'Additional Stop Statuses',
    name: 'additional_stop_statuses',
    type: 'checkboxGroup',
    options: [
      {
        value: 'WARM_OUTBOUND_TRANSFER_STARTED',
        label: 'Warm Outbound Transfer Started',
      },
      { value: 'MANUAL_DIAL_PREPARED', label: 'Manual Dial Prepared' },
    ],
    defaultValue: [],
  },
  {
    label: 'Subdomain',
    name: 'subdomain',
    type: 'text',
    required: true,
    disabled: false,
    default: 'cbf',
  },
]

const regalSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', false),
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'campaign_name', label: 'Campaign Name' },
      { value: 'queue', label: 'Queue' },
      { value: 'direction', label: 'Direction' },
      { value: 'journey_name', label: 'Journey Name' },
      { value: 'campaign_name_journey_name', label: 'Campaign Name + Journey Name' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'campaign_name',
  },
]

const dialpadSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'Api Key',
    name: 'api_key',
    type: 'text',
    required: true,
    disabled: false,
    nullable: false,
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'group_id', label: 'Group ID' },
      { value: 'group_id+direction', label: 'Group ID + Direction' },
    ],
    defaultValue: 'direction',
  },
  {
    label: 'VOIP Call ID',
    name: 'voip_call_id',
    type: 'select',
    options: [
      { value: 'call_id', label: 'Call ID' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'call_id',
    disabled: true,
  },
  {
    label: 'VOIP Customer ID',
    name: 'voip_customer_id',
    type: 'select',
    options: [
      { value: 'contact.id', label: 'Contact ID' },
      { value: 'contact.phone', label: 'Contact Phone' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'contact.id',
  },
  {
    label: 'Start on Ringing',
    name: 'start_on_ringing',
    type: 'checkbox',
    defaultValue: false,
  },
]

const callTrackingMetricsSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'Access Key (or username)',
    name: 'username',
    type: 'text',
    required: true,
  },
  {
    label: 'Secret Key (or password)',
    name: 'password',
    type: 'password',
    required: true,
  },
  {
    label:
      'Account ID (for multiple accounts with same creds, input a comma separated list with no spaces)',
    name: 'account_id',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Call Direction' },
      { value: 'queue', label: 'Queue' },
      { value: '', label: 'None' },
    ],
    defaultValue: 'direction',
  },
]

const vonageContactCenterSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', true),
  {
    label: 'Client ID',
    name: 'client_id',
    type: 'text',
    required: true,
  },
  {
    label: 'Client Secret',
    name: 'client_secret',
    type: 'password',
    required: true,
  },
  {
    label: 'Region',
    name: 'region',
    type: 'select',
    options: [
      { value: 'usa', label: 'North America' },
      { value: 'emea', label: 'Europe, Middle East, and Africa' },
      { value: 'apac', label: 'Asia-Pacific' },
    ],
    defaultValue: 'usa',
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'addressName', label: 'Address' },
      { value: 'mappingName', label: 'Mapping' },
      { value: 'interactionPlanName', label: 'Interaction Plan' },
      { value: 'reportingGroup', label: 'Reporting Group' },
      { value: 'serviceName', label: 'Service Name' },
    ],
    defaultValue: 'direction',
  },
]

const broadworksXsiSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_voip_user_id', true),
  {
    label: 'Username',
    name: 'username',
    type: 'text',
    required: true,
  },
  {
    label: 'Password',
    name: 'password',
    type: 'password',
    required: true,
  },
  {
    label: 'XSP Base URL',
    name: 'xsp_base_url',
    type: 'text',
    required: true,
  },
  {
    label: 'Enterprise ID',
    name: 'enterprise_id',
    type: 'text',
    required: true,
  },
  {
    label: 'VOIP Campaign Name',
    name: 'voip_campaign_name',
    type: 'select',
    options: [
      { value: 'direction', label: 'Direction' },
      { value: 'hunt_group', label: 'Hunt Group' },
      // haven't gotten examples where queue is present so not sure if it works
      // { value: 'queue', label: 'Queue' },
    ],
    defaultValue: 'direction',
  },
]

// A section heading. `displayOnly` marks it (and the read-only panels) for
// stripDisplayOnlyFields: AbstractForm seeds an initial value for EVERY schema entry,
// and IntegrationsSettings PUTs `values` wholesale, so without stripping them the
// headings are sent to the backend and every no-op Save reports them as changes.
const heading = (name, label) => ({
  isStatic: true,
  displayOnly: true,
  as: 'h4',
  name,
  label,
})

const genericSchema = [
  ...baseConnector,
  {
    raw: true,
    displayOnly: true,
    name: 'generic_webhook_instructions',
    as: <GenericWebhookInstructions key="generic-webhook" />,
  },
  {
    raw: true,
    displayOnly: true,
    name: 'generic_setup_guide',
    as: <GenericSetupGuide key="generic-setup-guide" />,
  },
  {
    label: 'Vendor',
    name: GENERIC_FIELDS.vendor,
    type: 'text',
    required: false,
    nullable: true,
    placeholder: 'kixie',
  },

  heading('generic_heading_agent', 'Agent identity'),
  ...createStartStopSelect('by_email', true),
  {
    raw: true,
    name: GENERIC_FIELDS.agentIdPath,
    defaultValue: '',
    customValidators: [requiredPathSchema('Agent identifier path')],
    as: (
      <PathField
        key="generic-agent-id"
        name={GENERIC_FIELDS.agentIdPath}
        label="Agent Identifier Path"
        required
        placeholder="agent.email"
        description="Where the agent's email or VOIP user ID lives, matching the Start Stop Method above."
      />
    ),
  },

  heading('generic_heading_startstop', 'Start and stop'),
  {
    raw: true,
    name: GENERIC_FIELDS.eventTypePath,
    defaultValue: '',
    customValidators: [requiredPathSchema('Event type path')],
    as: (
      <PathField
        key="generic-event-type"
        name={GENERIC_FIELDS.eventTypePath}
        label="Event Type Path"
        required
        placeholder="eventType"
        description="The field that says what happened. Its value decides whether the event starts a call, stops one, carries only a disposition, or is ignored."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.startValues,
    defaultValue: [],
    customValidators: [START_VALUES_SCHEMA],
    as: (
      <GenericEventValuesFields
        key="generic-start-values"
        field={GENERIC_FIELDS.startValues}
        label="Values that START a call"
        testIdPrefix="generic-start"
        placeholder="callAnswered"
        caption="Matched ignoring case and surrounding spaces. A numeric code works too — enter it as text."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.stopValues,
    defaultValue: [],
    customValidators: [STOP_VALUES_SCHEMA],
    as: (
      <GenericEventValuesFields
        key="generic-stop-values"
        field={GENERIC_FIELDS.stopValues}
        label="Values that STOP a call"
        testIdPrefix="generic-stop"
        placeholder="callEnded"
        caption="A value cannot appear in more than one list. Anything matching no list is ignored."
      />
    ),
  },

  heading('generic_heading_call', 'Call identity'),
  {
    raw: true,
    name: GENERIC_FIELDS.callIdPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-call-id"
        name={GENERIC_FIELDS.callIdPath}
        label="VOIP Call ID Path"
        placeholder="callId"
        description="The vendor's own call identifier, and it must be the same on the start and the stop event. Without it, duplicate and out-of-order stop events cannot be filtered."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.customerIdPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-customer-id"
        name={GENERIC_FIELDS.customerIdPath}
        label="VOIP Customer ID Path"
        placeholder="customer.number"
        description="Optional. Usually the customer phone number or account number."
      />
    ),
  },

  heading('generic_heading_disposition', 'Disposition'),
  {
    raw: true,
    name: GENERIC_FIELDS.dispositionPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-disposition"
        name={GENERIC_FIELDS.dispositionPath}
        label="Disposition Path"
        placeholder="disposition"
        description="Optional. Where the call outcome is on the payload. Read off stop events, and off the disposition-only events configured below."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.dispositionValues,
    defaultValue: [],
    customValidators: [DISPOSITION_VALUES_SCHEMA],
    as: (
      <GenericEventValuesFields
        key="generic-disposition-values"
        field={GENERIC_FIELDS.dispositionValues}
        label="Values for a DISPOSITION-only event"
        testIdPrefix="generic-disposition-values"
        placeholder="dispositions"
        caption="Only for vendors that report the outcome on a separate event. Such an event records the disposition and does not start or stop the call. If the vendor's disposition arrives on the event that ends the call, leave this empty and put that value under STOP instead."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.dispositionCallIdPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-disposition-call-id"
        name={GENERIC_FIELDS.dispositionCallIdPath}
        label="Disposition Call ID Path"
        placeholder="operator_call_id"
        description="Only if a disposition-only event puts the call id somewhere else. Defaults to the VOIP Call ID Path above. A disposition with no call id is not recorded."
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.dispositionAgentIdPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-disposition-agent-id"
        name={GENERIC_FIELDS.dispositionAgentIdPath}
        label="Disposition Agent Path"
        placeholder="operator_target.email"
        description="Only if a disposition-only event puts the agent somewhere else. Defaults to the Agent Path above. Used to attribute the outcome to the right organization."
      />
    ),
  },

  heading('generic_heading_campaign', 'Campaign name'),
  {
    raw: true,
    name: GENERIC_FIELDS.campaignNameFields,
    defaultValue: [],
    customValidators: [CAMPAIGN_PATHS_SCHEMA],
    as: <GenericCampaignNameFields key="generic-campaign-name" />,
  },

  heading('generic_heading_direction', 'Direction'),
  {
    raw: true,
    name: GENERIC_FIELDS.directionPath,
    defaultValue: '',
    customValidators: [optionalPathSchema],
    as: (
      <PathField
        key="generic-direction-path"
        name={GENERIC_FIELDS.directionPath}
        label="Direction Path"
        placeholder="direction"
      />
    ),
  },
  {
    raw: true,
    name: GENERIC_FIELDS.directionMap,
    defaultValue: [],
    customValidators: [DIRECTION_MAP_SCHEMA],
    as: <GenericDirectionMapFields key="generic-direction-map" />,
  },

  heading('generic_heading_metadata', 'Call metadata'),
  {
    raw: true,
    name: GENERIC_FIELDS.callMetadataFields,
    defaultValue: [],
    customValidators: [GENERIC_CALL_METADATA_SCHEMA],
    as: <GenericCallMetadataFields key="generic-call-metadata" />,
  },
]

const intermediaUniteSchema = [
  ...baseConnector,
  ...createStartStopSelect('by_email', true),
  {
    label: 'Client ID',
    name: 'client_id',
    type: 'text',
    required: true,
  },
  {
    label: 'Client Secret',
    name: 'client_secret',
    type: 'password',
    required: true,
  },
  {
    // Ordered sources joined into the campaign name. An empty list keeps the
    // default -- Dialed Number then Hunt Group -- which is what INTG-241 specified.
    raw: true,
    name: 'voip_campaign_name_fields',
    defaultValue: [],
    customValidators: [INTERMEDIA_UNITE_CAMPAIGN_FIELDS_SCHEMA],
    as: <IntermediaUniteCampaignNameFields key="intermedia-unite-campaign-name" />,
  },
]

export const configTypeToSchemaMap = {
  generic: genericSchema,
  ringcentral: ringcentralSchema,
  ringcentral_engage: ringcentralEngageSchema,
  genesys: genesysSchema,
  five9: five9Schema,
  five9_ess: five9EssSchema,
  '8x8': eightByEightSchema,
  '8x8_v2': eightByEightV2Schema,
  slack: slackSchema,
  vonage_enterprise: vonageEnterpriseSchema,
  amazon_connect: amazonConnectSchema,
  zoom: zoomSchema,
  zoom_video: zoomVideoSchema,
  zoom_contact_center: zoomContactCenterSchema,
  close: closeSchema,
  aircall: aircallSchema,
  tld: tldSchema,
  trackdrive: trackdriveSchema,
  sinch: sinchSchema,
  thrio: thrioSchema,
  nice: niceSchema,
  tcn: tcnSchema,
  regal: regalSchema,
  dialpad: dialpadSchema,
  dialpad_websocket: dialpadSchema,
  call_tracking_metrics: callTrackingMetricsSchema,
  mightycall: mightyCallSchema,
  goto: gotoSchema,
  atscall: atscallSchema,
  vonage_contact_center: vonageContactCenterSchema,
  broadworks_xsi: broadworksXsiSchema,
  intermedia_unite: intermediaUniteSchema,
}

export const getEditSchema = (type) => {
  return configTypeToSchemaMap[type]
}

export const buildDefaults = (integration, schema) => {
  const updatedIntegration = { ...integration }
  schema?.forEach((fieldOptions) => {
    if (fieldOptions.defaultValue && integration[fieldOptions.name] == null) {
      updatedIntegration[fieldOptions.name] = fieldOptions.defaultValue
    }
    if (fieldOptions.name === 'organization_id') {
      if (updatedIntegration[fieldOptions.name] >= 90000) {
        fieldOptions.disabled = false
      }
    }
    if (
      fieldOptions.json &&
      updatedIntegration[fieldOptions.name] != null &&
      typeof updatedIntegration[fieldOptions.name] === 'object'
    ) {
      updatedIntegration[fieldOptions.name] = JSON.stringify(
        updatedIntegration[fieldOptions.name],
        null,
        2
      )
    }
  })
  return updatedIntegration
}
